/**
 * The tournament page's addresses — the ONE owner of every `/edition/…` URL
 * shape that names a round or a board (O5, owner 2026-09-22).
 *
 * `/edition/<ed>` is the only page an event has. A round is a filter on it,
 * `?round=<roundId>`; a watched board is the board layer on top of it,
 * `?round=<roundId>&board=<n>`. `board` is the round's OWN board number, not a
 * game id: it is the key every producer already writes (the social poster's
 * `?board=<board_id>`, the merged round's `boardHref`, the heat cards), it is
 * what the relay stream addresses, and it exists from the first pairing — a
 * game id only exists once a pack publishes the round, so a board link minted
 * during the round would have nothing to point at. The pair (round id, board
 * number) is stable from pairing to archive.
 *
 * Pure and dependency-free on purpose: the `/round/:id` Pages Function
 * (`functions/round/[id].ts`) imports {@link legacyRoundDestination} too, so the
 * server's 301 and the client's fallback redirect compute one answer.
 */

/** The query key that names the watched board; its presence opens the board layer. */
export const BOARD_PARAM = "board";
/** The query key that names a round: the finder's round filter, and the board layer's round. */
export const ROUND_PARAM = "round";

/** Parameters an old `/round/<id>` link carried that have no meaning on the edition page. */
const DROPPED_LEGACY_PARAMS = new Set(["edition", ROUND_PARAM]);

/** `/edition/<ed>`, with an optional query. */
export function editionHref(editionId: string, params?: URLSearchParams): string {
  const base = `/edition/${encodeURIComponent(editionId)}`;
  const search = params?.toString() ?? "";
  return search === "" ? base : `${base}?${search}`;
}

/** One round of the tournament: the finder filtered to it. */
export function editionRoundHref(editionId: string, roundId: string): string {
  return editionHref(editionId, new URLSearchParams({ [ROUND_PARAM]: roundId }));
}

/**
 * The board layer. `board` null = "Watch round": the viewer opens on its own
 * choice of board (the followed federation's, else the round's default), and
 * the parameter is written empty so the layer still opens.
 */
export function boardViewHref(editionId: string, roundId: string, board: number | null): string {
  return editionHref(editionId, new URLSearchParams({ [ROUND_PARAM]: roundId, [BOARD_PARAM]: board === null ? "" : `${board}` }));
}

/** Does this edition URL ask for the board layer? It needs a round to watch. */
export function isBoardView(query: URLSearchParams): boolean {
  return query.has(BOARD_PARAM) && (query.get(ROUND_PARAM) ?? "") !== "";
}

/**
 * Where an old `/round/<roundId>?…` link goes once its edition is known.
 *
 * State-independent on purpose, so the answer can be a PERMANENT redirect: the
 * round→edition mapping never changes (`relay-control/src/calendar.rs`
 * `edition_id_of`), but the round's state does, and a browser that cached a
 * state-dependent 301 would replay a stale answer. So the round always rides
 * along as `?round=`, and a `board` (the poster's links, heat cards, merged
 * `boardHref`) opens the board layer. Everything else the link carried — `ply`,
 * `3d`, `set`, the poster's `utm_*` — passes through untouched; only the
 * `?edition=` hint (now the path) and a stray `round` are dropped.
 */
export function legacyRoundDestination(roundId: string, editionId: string, query: URLSearchParams): string {
  const params = new URLSearchParams();
  params.set(ROUND_PARAM, roundId);
  if (query.has(BOARD_PARAM)) params.set(BOARD_PARAM, query.get(BOARD_PARAM) ?? "");
  for (const [key, value] of query) {
    if (key === BOARD_PARAM || DROPPED_LEGACY_PARAMS.has(key)) continue;
    params.append(key, value);
  }
  return editionHref(editionId, params);
}
